import { preloadContentHeader } from '@confluence/content-header/entry-points/preloadContentHeader.ts';
// @ts-ignore-next-line Ignore error that preloadContentTypesHeader has no types, it's a JS file
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { preloadCustomHeaderAndFooter } from '@confluence/custom-header-footer/entry-points/preloadCustomHeaderAndFooter.ts';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties.ts';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';
import { expVal, expValEquals, UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { preloadFocusedInlineComment } from '@confluence/inline-comments/entry-points/preloadFocusedInlineComment.ts';
import { preloadEditorData } from '@confluence/load-edit-page/entry-points/preloadEditorData.ts';
import { preloadRecordLoomButton } from '@confluence/loom-utils/entry-points/preloadRecordLoomButton.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask.ts';
import { preloadPageReactions } from '@confluence/reactions/entry-points/preloadPageReactions.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { preloadContentPrerequisites } from '@confluence/content-prerequisites/entry-points/preloadContentPrerequisites.ts';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points/index.ts';
import { preloadPageStatusQuery } from '@confluence/query-preloaders-common/entry-points/preloadPageStatusQuery.ts';
import { preloadLandUsersInEditorWithRevampedBpqa } from '@confluence/onboarding-helpers/entry-points/preloadLandUsersInEditorWithRevampedBpqa.ts';
import { preloadLegacyBridge } from '@confluence/query-preloaders-common/entry-points/preloadLegacyBridge.ts';
import { preloadEndOfPageRecommendation } from '@confluence/page-recommendations/entry-points/preloadEndOfPageRecommendation.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import {
	ATL_PAGE_CONTENT_FOOTER_ACTIONS,
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import {
	preloadWebPanelLocation,
	preloadWebPanelLocations,
} from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation.ts';
import { preloadAIFloatingContextMenu } from '@confluence/ai-floating-context-menu/entry-points/preloadAIFloatingContextMenu.ts';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { preloadLivePageData } from '@confluence/load-live-page/entry-points/preloadLivePageData.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { FORGE_MODULE_XEN_MACRO } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadFooterComments as legacyPreloadFooterComments } from '@confluence/comments-panel-queries/entry-points/preloadFooterComments.ts';
import { preloadObjectComments } from '@confluence/object-comments/entry-points/preloadObjectComments.ts';
import { preloadAddCommentPermissionCheck } from '@confluence/comments-panel-queries/entry-points/preloadAddCommentPermissionCheck.ts';
import {
	getIsEmbeddedConfluence,
	getUsername,
	hasEmbeddedAllowedFeatures,
} from '@confluence/query-preloaders-common/entry-points/matchHelpers.ts';
import { matchBlogPage } from '@confluence/query-preloaders-common/entry-points/matchRoutes.ts';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks.ts';
import { preloadSmartCreateFeedbackBanner } from '@confluence/editor-action-bar/entry-points/preloadSmartCreateFeedbackBanner.ts';
import { preloadAllOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadAllOnboardingState.ts';
import { preloadSpaceEnabledContentTypes } from '@confluence/create-blank-fabric-page/entry-points/preloadSpaceEnabledContentTypes.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

import { preloadConnectedThirdPartyConfig } from './preloadConnectedThirdPartyConfig.ts';

/*
 * preloadLivePageRoute preload queries are currently arranged in order of latency, with slowest
 * queries pushed to the tasks array first.
 */
export const preloadLivePageRoute = async (
	match: RouteMatch,
	url: string,
): Promise<void | any[] | Error[]> => {
	const {
		isLicensed,
		userId,
		edition,
		cloudId,
		activationId,
		accessStatus,
		isLoggedIn,
		isAdminHubAIEnabled,
		environment,
	} = getPreloaderFnContext();
	const tasks: Promise<any>[] = [];
	const { spaceKey, contentId } = match.params;
	const username = getUsername(match);
	const isEmbeddedConfluence = getIsEmbeddedConfluence(match);
	const isBlog = Boolean(matchBlogPage(url));
	const shouldPreloadNavigation = !isEmbeddedConfluence;

	if (expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true)) {
		// During 'platform_editor_editor_ssr_streaming' remove this variable and use preloadLivePageData directly
		// in tasks.push
		const preloadPromise = preloadLivePageData({
			userId,
			contentId,
			spaceKey,
			cloudId,
		});

		tasks.push(
			isSSRStreaming()
				? createDataFetcherForSuspense('content-main', () => preloadPromise)
				: preloadPromise,
		);
	} else {
		// During 'platform_editor_editor_ssr_streaming' remove this variable and use prepareGuardExceptionTask directly
		// in tasks.push
		const preloadPromise = prepareGuardExceptionTask('EditorData', () =>
			preloadEditorData({
				spaceKey,
				contentId,
				shouldPreloadMentionsProvider: true,
				useCache: false,
				cloudId,
				isBlog,
				isLivePage: true,
			}),
		);

		tasks.push(
			isSSRStreaming()
				? createDataFetcherForSuspense('content-main', () => preloadPromise)
				: preloadPromise,
		);
	}

	// Warm the third-party connector-config *code chunk* off the editor's critical
	// path so `useConnectedThirdPartyProductKeys` has its module ready at editor
	// mount. This intentionally does NOT issue the connector-config network request
	// — doing so here plants a stale-while-revalidate cache entry that the
	// editor-mount hook then serves stale from, wedging the quick-insert menu on a
	// partial app list. See preloadConnectedThirdPartyConfig for the full rationale.
	// Client-only, scoped to the `confluence_quick_insert_embeds` experiment cohort.
	// Read without exposure — the authoritative exposure fires inside the provider
	// builder at editor mount. Fire-and-forget: intentionally not pushed into `tasks`.
	if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR &&
		UNSAFE_expValNoExposure('confluence_quick_insert_embeds', 'cohort', 'control') !== 'control'
	) {
		preloadConnectedThirdPartyConfig();
	}

	// 1. Byline and possibly content prerequisites
	tasks.push(
		preloadContentHeader({
			spaceKey,
			contentId,
			username,
			edition,
			isPrevEditRoute: false,
			useNewContentTopper: true,
			isLivePageRoute: true,
		}).then((resultOrHasErrorBoolean) => {
			if (typeof resultOrHasErrorBoolean === 'boolean') {
				if (resultOrHasErrorBoolean) {
					return preloadContentPrerequisites(contentId, spaceKey);
				}
			} else {
				const { results, hasErrors } = resultOrHasErrorBoolean;
				if (!hasErrors) {
					return results;
				}
				return preloadContentPrerequisites(contentId, spaceKey);
			}
		}),
		preloadLegacyBridge({ contentId, isBlog }),
	);
	if (!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true)) {
		tasks.push(preloadPageStatusQuery(contentId));
	}

	// 2. Page title
	!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
		tasks.push(preloadPageTitleContentProperties({ contentId, spaceKey }));

	// 2a. Space enabled content types
	if (spaceKey) {
		tasks.push(preloadSpaceEnabledContentTypes(spaceKey));
	}

	// 3. Space details
	!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
		tasks.push(preloadSpaceDetail(spaceKey));

	if (isSSRStreaming()) {
		tasks.push(
			createDataFetcherForSuspense('content-types-header', () => {
				const contentTypesHeaderPreloadTasks: Promise<any>[] = [];

				// 4. Content types header (object header)
				contentTypesHeaderPreloadTasks.push(preloadContentTypesHeader(contentId, true));
				// (No need to call preloadPageRestrictionsQuery() here; it will be called by
				//  preloadContentHeader() above)

				// 4a. Unified Share and Restrict Button
				if (
					!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
					fg('confluence_frontend_usd_ssr')
				) {
					contentTypesHeaderPreloadTasks.push(preloadShareAndRestrictButtonQuery(contentId));
				}

				return contentTypesHeaderPreloadTasks;
			}),
		);

		// 5. Navigation and page tree
		if (shouldPreloadNavigation) {
			tasks.push(
				createDataFetcherForSuspense('side-navigation', () => {
					return preloadNavigationTasks(spaceKey, contentId, isLicensed, isBlog);
				}),
			);
		}
	} else {
		tasks.push(preloadContentTypesHeader(contentId, true));
		// (No need to call preloadPageRestrictionsQuery() here; it will be called by
		//  preloadContentHeader() above)

		if (
			!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
			fg('confluence_frontend_usd_ssr')
		) {
			tasks.push(preloadShareAndRestrictButtonQuery(contentId));
		}

		if (shouldPreloadNavigation) {
			tasks.push(preloadNavigationTasks(spaceKey, contentId, isLicensed, isBlog));
		}
	}

	// 6. Inline Comments
	if (match?.query.focusedCommentId) {
		tasks.push(preloadFocusedInlineComment(match?.query.focusedCommentId as string, contentId));
	}

	// 7. Reactions
	if (!isEmbeddedConfluence || hasEmbeddedAllowedFeatures('page-reactions', match)) {
		// TODO: delete
		// Preload whether the server has enabled reactions at all to show placeholder
		tasks.push(preloadWebPanelLocation({ contentId, location: ATL_PAGE_CONTENT_FOOTER_ACTIONS }));
		// Preload reactions asynchronously so SSR won't wait for it
		// it's best effort preloading so if it preloads then we have the data, otherwise SPA can load it
		tasks.push(
			preloadPageReactions({
				pageId: contentId,
				containerId: spaceKey,
			}),
		);
	}

	// 10. Stuff only needed for initial load
	tasks.push(
		// We only refresh token when it is about to expire.
		preloadMediaToken(contentId),
	);
	!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
		void preloadCustomHeaderAndFooter(spaceKey);

	// Preload onboarding state for live page
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
			accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS) &&
		expVal('confluence_centralized_onboarding_state', 'isEnabled', false)
	) {
		tasks.push(preloadAllOnboardingState());
	}

	if (expValEquals('web_panel_query_batch_call', 'isEnabled', true)) {
		tasks.push(
			preloadWebPanelLocations({
				contentId,
				locations: [ATL_GENERAL, ATL_FOOTER],
			}),
		);
	} else {
		tasks.push(
			preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
			preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
		);
	}

	// 11. Object Sidebar Control + AI Button
	if (isAdminHubAIEnabled) {
		if (expValEquals('reduce_queries_in_default_suspense', 'isEnabled', true)) {
			tasks.push(
				createDataFetcherForSuspense('ai-floating-context-menu', () =>
					preloadAIFloatingContextMenu(contentId, isBlog),
				),
			);
		} else {
			tasks.push(preloadAIFloatingContextMenu(contentId, isBlog));
		}
	}

	// 12. Preload Loom button in Action Bar
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		isLoggedIn
	) {
		tasks.push(
			preloadRecordLoomButton({
				cloudId,
				activationId,
				accessStatus,
				isLoggedIn,
				userId,
				environment,
			}),
		);
	}

	// 14. Preload forge extension manifest
	if (fg('confluence_preload_forge_viewport_heights_editor')) {
		tasks.push(
			preloadUseExtensionList({
				contentId,
				spaceKey,
				moduleType: FORGE_MODULE_XEN_MACRO,
			}),
		);
	}

	// 15. Preload footer comments
	const preloadObjectCommentsTask = () =>
		expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? preloadObjectComments(contentId)
			: legacyPreloadFooterComments(contentId);

	if (expValEquals('reduce_queries_in_default_suspense', 'isEnabled', true)) {
		tasks.push(
			createDataFetcherForSuspense('object-comments', () => [
				preloadObjectCommentsTask(),
				preloadAddCommentPermissionCheck(contentId),
			]),
		);
	} else {
		tasks.push(preloadObjectCommentsTask());
		// 16. Preload add comment permission check
		tasks.push(preloadAddCommentPermissionCheck(contentId));
	}

	// 17. Recommendations
	// Only preload recommendations for authenticated users (knowledge_discovery service requires auth)
	if (isLoggedIn) {
		const recommendationsTimeout = StatsigConfigurations.getNumber(
			'confluence_frontend_preload_recommendation_timeout',
			SLOW_QUERY_DEFAULT_TIMEOUT,
		);
		tasks.push(
			slowQueryGuard(
				'preloadEndOfPageRecommendation',
				() => {
					return preloadEndOfPageRecommendation({
						contentId,
						spaceKey,
						contentType: isBlog ? 'blogpost' : 'page',
					});
				},
				recommendationsTimeout,
			),
		);
	}

	//18. Preload Smart Create Feedback Banner
	tasks.push(preloadSmartCreateFeedbackBanner(contentId));

	//19. Add Onboarding State for revamped bpqa
	tasks.push(preloadLandUsersInEditorWithRevampedBpqa());

	return waitForAllReturnWhenError(tasks);
};
